/**
 * User Management Service
 *
 * Secure service for managing user accounts with comprehensive security measures.
 * Implements role-based access control, input validation, and secure API calls.
 *
 * SECURITY FEATURES:
 * - Admin-only access enforced via Row-Level Security (RLS)
 * - Input validation and sanitization for all user data
 * - Email format validation before API calls
 * - Prevention of SQL injection via parameterized queries (Supabase)
 * - Secure password handling (never exposed in responses)
 * - Error sanitization to prevent information leakage
 * - Rate limiting for user operations
 * - Soft delete instead of hard delete for audit trail
 *
 * @module services/userService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { validateEmail, sanitizeInput, sanitizeObject, RateLimiter } from '../utils/security';
import { callMcpTool } from './mcpClient';
import { passwordPolicyErrors } from '../utils/passwordPolicy';

/**
 * Valid user roles
 * SECURITY: Enforced at database level with CHECK constraint
 * (see supabase/migrations/060_role_supervisor.sql,
 *  20260811090000_draft_agregar_roles_operario_mensajero.sql for the
 *  canonical list and the per-role RLS policies). Keep this allowlist in
 *  sync with mcp-server/src/tools/userTools.js. 'lider' is NOT here on
 *  purpose — it's a project_leaders assignment, not a profiles.role value
 *  (#463).
 */
const VALID_ROLES = ['admin', 'supervisor', 'technician', 'operario', 'mensajero', 'client'];

/**
 * Rate limiter for user operations
 * SECURITY: Prevent abuse of user management operations
 * Max 10 operations per minute per admin
 */
const userOperationLimiter = new RateLimiter(10, 60000);

/**
 * Allowed fields for user profile updates
 * SECURITY: Allowlist approach prevents unauthorized field modifications
 */
const ALLOWED_PROFILE_FIELDS = [
  'full_name',
  'phone',
  'company_name',
  'role',
];

/**
 * Verify current user is an admin
 * SECURITY: Check admin role before allowing user management operations
 * Note: RLS policies at database level provide defense in depth
 * @returns {Promise<{isAdmin: boolean, userId: string, error}>}
 */
const verifyAdminAccess = async () => {
  try {
    // SECURITY: Get authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!user) {
      return {
        isAdmin: false,
        userId: null,
        error: new Error('Authentication required'),
      };
    }

    // SECURITY: Verify admin role from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (profileError) throw profileError;

    if (profile.role !== 'admin') {
      return {
        isAdmin: false,
        userId: user.id,
        error: new Error('Admin access required'),
      };
    }

    return {
      isAdmin: true,
      userId: user.id,
      error: null,
    };
  } catch (error) {
    console.error('[USER_SERVICE] Admin verification error:', error.message);
    return {
      isAdmin: false,
      userId: null,
      error: new Error('Access verification failed'),
    };
  }
};

/**
 * Validate user data before database operations
 * SECURITY: Comprehensive input validation to prevent injection and data corruption
 * @param {Object} userData - User data to validate
 * @param {boolean} isCreate - Whether this is for user creation
 * @returns {Object} Validation result
 */
const validateUserData = (userData, isCreate = false) => {
  const errors = [];

  // SECURITY: Validate email (required for creation)
  if (isCreate || userData.email !== undefined) {
    if (!userData.email) {
      errors.push('Email is required');
    } else {
      const emailValidation = validateEmail(userData.email);
      if (!emailValidation.valid) {
        errors.push(emailValidation.error);
      }
    }
  }

  // SECURITY: Validate full_name (required)
  if (isCreate || userData.full_name !== undefined) {
    if (!userData.full_name || typeof userData.full_name !== 'string') {
      errors.push('Full name is required');
    } else if (userData.full_name.trim().length < 2) {
      errors.push('Full name must be at least 2 characters');
    } else if (userData.full_name.trim().length > 100) {
      errors.push('Full name must not exceed 100 characters');
    }
  }

  // SECURITY: Validate role (required, must be in VALID_ROLES)
  if (isCreate || userData.role !== undefined) {
    if (!userData.role) {
      errors.push('Role is required');
    } else if (!VALID_ROLES.includes(userData.role)) {
      errors.push(`Role must be one of: ${VALID_ROLES.join(', ')}`);
    }
  }

  // SECURITY: Validate phone (optional, but must be valid if provided)
  if (userData.phone && typeof userData.phone === 'string') {
    const phoneDigits = userData.phone.replace(/[^0-9]/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      errors.push('Invalid phone number length');
    }
  }

  // SECURITY: Validate company_name (optional)
  if (userData.company_name && typeof userData.company_name === 'string') {
    if (userData.company_name.trim().length > 200) {
      errors.push('Company name must not exceed 200 characters');
    }
  }

  // SECURITY: Validate password (required for creation only)
  if (isCreate) {
    if (!userData.password || typeof userData.password !== 'string') {
      errors.push('Password is required');
    } else if (userData.password.length < 8) {
      errors.push('Password must be at least 8 characters');
    } else if (userData.password.length > 72) {
      // SECURITY: bcrypt max length is 72 characters
      errors.push('Password must not exceed 72 characters');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Sanitize user data before database operations
 * SECURITY: Remove XSS vectors and normalize data
 * @param {Object} userData - User data to sanitize
 * @returns {Object} Sanitized user data
 */
const sanitizeUserData = (userData) => {
  const sanitized = {};

  // SECURITY: Sanitize and normalize email
  if (userData.email) {
    const emailValidation = validateEmail(userData.email);
    sanitized.email = emailValidation.sanitized;
  }

  // SECURITY: Sanitize text fields
  if (userData.full_name) {
    sanitized.full_name = sanitizeInput(userData.full_name, {
      maxLength: 100,
      allowNewlines: false,
    });
  }

  if (userData.phone) {
    sanitized.phone = sanitizeInput(userData.phone, {
      maxLength: 20,
      allowNewlines: false,
    });
  }

  if (userData.company_name) {
    sanitized.company_name = sanitizeInput(userData.company_name, {
      maxLength: 200,
      allowNewlines: false,
    });
  }

  // SECURITY: Role validation (allowlist)
  if (userData.role && VALID_ROLES.includes(userData.role)) {
    sanitized.role = userData.role;
  }

  return sanitized;
};

/**
 * Get all users
 * SECURITY: Admin-only operation, enforced by RLS and explicit check
 * @param {Object} options - Query options
 * @param {string} options.role - Filter by role
 * @param {string} options.search - Search by name or email
 * @param {boolean} options.activeOnly - Only return active users (default: true)
 * @returns {Promise<{data, error}>}
 */
export const getUsers = async (options = {}) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Build query with parameterized filters
    let query = supabase
      .from('profiles')
      .select('id, role, full_name, phone, company_name, created_at, updated_at')
      .order('created_at', { ascending: false });

    // SECURITY: Apply filters (parameterized via Supabase)
    if (options.role && VALID_ROLES.includes(options.role)) {
      query = query.eq('role', options.role);
    }

    // SECURITY: Search filter with sanitization
    if (options.search && typeof options.search === 'string') {
      const searchTerm = sanitizeInput(options.search, {
        maxLength: 100,
        allowNewlines: false,
      });
      // SECURITY: Use .ilike for case-insensitive search (parameterized by Supabase)
      // #739/#742 — término del módulo compartido; el texto humano va contra su
      // columna generada y los códigos y emails quedan crudos.
      const termino = terminoParaFiltro(searchTerm);
      query = query.or(
        `${columnaNormalizada('full_name')}.ilike.%${termino}%,`
        + `${columnaNormalizada('company_name')}.ilike.%${termino}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    // SECURITY: Never return sensitive data (passwords are not in profiles table)
    return { data, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Get users error:', error.message);
    // SECURITY: Return generic error message to prevent information leakage
    return {
      data: null,
      error: new Error('Failed to retrieve users'),
    };
  }
};

/**
 * Get user by ID
 * SECURITY: Admin-only operation, RLS enforced
 * @param {string} userId - User ID (UUID)
 * @returns {Promise<{data, error}>}
 */
export const getUserById = async (userId) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Validate UUID format to prevent injection
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid user ID');
    }

    // SECURITY: UUID validation (basic format check)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      throw new Error('Invalid user ID format');
    }

    // SECURITY: Parameterized query via Supabase
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, full_name, phone, company_name, created_at, updated_at')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('User not found');
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Get user by ID error:', error.message);
    return {
      data: null,
      error: new Error(error.message === 'User not found' ? error.message : 'Failed to retrieve user'),
    };
  }
};

/**
 * Create new user account
 * SECURITY: Admin-only operation, creates auth user + profile
 * @param {Object} userData - User data
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 * @param {string} userData.full_name - Full name
 * @param {string} userData.role - User role (admin|employee|client)
 * @param {string} userData.phone - Phone number (optional)
 * @param {string} userData.company_name - Company name (optional, for clients)
 * @returns {Promise<{data, error}>}
 */
export const createUser = async (userData) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, userId: adminId, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Rate limiting check
    if (userOperationLimiter.isLimited(adminId)) {
      throw new Error('Rate limit exceeded. Please try again later');
    }

    // SECURITY: Validate input data
    const validation = validateUserData(userData, true);
    if (!validation.valid) {
      throw new Error(validation.errors.join('. '));
    }

    // SECURITY: Sanitize input data
    const sanitized = sanitizeUserData(userData);

    // Route via MCP create_user tool — uses Supabase Admin API with
    // email_confirm:true so the new user can log in immediately. Direct
    // supabase.auth.signUp left emails unconfirmed and blocked login (#148).
    // El transporte (token, 401 de #389, envelope success/result) es el
    // compartido: callMcpTool. Acá solo queda lo que ESTE call site hace
    // distinto — traducir el `{error}` del helper a un throw, porque el
    // contrato de createUser hacia afuera es "tira y el catch de abajo lo
    // convierte en {data, error}".
    const { data: newUser, error: mcpError } = await callMcpTool('create_user', {
      email: sanitized.email,
      password: userData.password,
      full_name: sanitized.full_name,
      role: sanitized.role,
      phone: sanitized.phone || null,
      company_name: sanitized.company_name || null
    });

    if (mcpError) {
      console.error('[USER_SERVICE] MCP create_user error:', mcpError.message);
      throw mcpError;
    }

    const newUserId = newUser?.id || newUser?.user_id;

    // SECURITY: Record rate limit attempt. Va acá, antes de cualquier retorno:
    // el intento se consumió apenas la creación de auth ocurrió, y cobrarlo
    // sólo en el camino feliz haría del fallo un bypass del rate limit.
    userOperationLimiter.recordAttempt(adminId);

    // #583 — EL PERFIL SALE DE LA BASE, NUNCA DEL INPUT.
    //
    // Hasta acá ya hubo UNA escritura: el usuario de auth existe. La tool MCP
    // `create_user` (mcp-server/src/tools/userTools.js) hace además el
    // `upsert` de `profiles` y devuelve la fila resultante en
    // `data.profile` — o sea, la verdad del servidor, leída del `.select()`
    // del propio upsert. Esa es la fuente preferida.
    //
    // Lo que había antes: si el re-`select` de `profiles` fallaba, un
    // `console.warn` y `{ data: {…armado con sanitized…}, error: null }`. La
    // pantalla decía "usuario creado" con el rol que el admin TECLEÓ, no con
    // el que quedó guardado, y el usuario podía loguearse igual. Misma clase
    // que #555 (`error: null` + warning → 179 de 191 tareas sin responsable):
    // un fallo de lectura degradado a aviso, con la respuesta fabricada desde
    // el input.
    //
    // Qué se hace ahora con el usuario de auth que YA quedó creado: **nada**.
    // No se borra. Un `select` que falla NO prueba que la fila no exista —
    // puede ser RLS, red o un timeout — y borrar una cuenta real por un
    // hipo de lectura es peor que el bug. Lo que sí se hace es DECIR el
    // estado real, para que el admin sepa por qué un reintento le va a
    // contestar "email already exists".
    const profileDelServidor = newUser?.profile || null;

    const { data: profileLeido, error: profileError } = profileDelServidor || !newUserId
      ? { data: profileDelServidor, error: null }
      : await supabase
          .from('profiles')
          .select('id, role, full_name, phone, company_name, created_at')
          .eq('id', newUserId)
          .single();

    const profile = profileDelServidor || profileLeido;

    if (!profile) {
      const detalle = profileError?.message || 'la consulta no devolvió ninguna fila';
      console.error('[USER_SERVICE] Perfil no verificado tras crear el usuario de auth:', {
        newUserId,
        detalle,
      });
      // Error, no aviso. El mensaje describe el estado REAL del sistema.
      throw new Error(
        `El usuario de autenticación se creó${newUserId ? ` (id ${newUserId})` : ''}, ` +
          `pero NO se pudo verificar su perfil: ${detalle}. ` +
          'El usuario quedó a medias: puede iniciar sesión, pero sin fila en `profiles` ' +
          'los chequeos de rol de RLS le fallan. Revisá /usuarios antes de reintentar ' +
          '(un reintento va a fallar con "email already exists").'
      );
    }

    // SECURITY: Return user data (no password)
    return { data: profile, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Create user error:', error.message);
    return {
      data: null,
      error: new Error(error.message || 'Failed to create user'),
    };
  }
};

/**
 * Update user profile
 * SECURITY: Admin-only operation, allowlist fields only
 * @param {string} userId - User ID to update
 * @param {Object} updates - Profile updates
 * @param {string} updates.full_name - Full name
 * @param {string} updates.phone - Phone number
 * @param {string} updates.company_name - Company name
 * @param {string} updates.role - User role
 * @returns {Promise<{data, error}>}
 */
export const updateUser = async (userId, updates) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, userId: adminId, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Rate limiting check
    if (userOperationLimiter.isLimited(adminId)) {
      throw new Error('Rate limit exceeded. Please try again later');
    }

    // SECURITY: Validate user ID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!userId || !uuidRegex.test(userId)) {
      throw new Error('Invalid user ID');
    }

    // SECURITY: Validate update data (not creation, so email/password not required)
    const validation = validateUserData(updates, false);
    if (!validation.valid) {
      throw new Error(validation.errors.join('. '));
    }

    // SECURITY: Sanitize and filter to allowed fields only
    const sanitized = sanitizeUserData(updates);
    const allowedUpdates = sanitizeObject(sanitized, ALLOWED_PROFILE_FIELDS);

    // SECURITY: Ensure we're not updating with empty object
    if (Object.keys(allowedUpdates).length === 0) {
      throw new Error('No valid fields to update');
    }

    // SECURITY: Prevent admin from downgrading their own role
    if (allowedUpdates.role && userId === adminId && allowedUpdates.role !== 'admin') {
      throw new Error('Cannot change your own admin role');
    }

    // SECURITY: Parameterized update via Supabase
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...allowedUpdates,
        updated_at: new Date().toISOString(), // SECURITY: Server-controlled timestamp
      })
      .eq('id', userId)
      .select('id, role, full_name, phone, company_name, created_at, updated_at')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('User not found');
      }
      throw error;
    }

    // SECURITY: Record rate limit attempt
    userOperationLimiter.recordAttempt(adminId);

    return { data, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Update user error:', error.message);
    return {
      data: null,
      error: new Error(error.message || 'Failed to update user'),
    };
  }
};

/**
 * Delete user (soft delete - sets active flag)
 * SECURITY: Admin-only operation, prevents deletion of own account
 * Note: Currently profiles don't have 'active' column, so this performs hard delete
 * TODO: Add 'active' column for soft delete audit trail
 * @param {string} userId - User ID to delete
 * @returns {Promise<{success, error}>}
 */
export const deleteUser = async (userId) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, userId: adminId, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Rate limiting check
    if (userOperationLimiter.isLimited(adminId)) {
      throw new Error('Rate limit exceeded. Please try again later');
    }

    // SECURITY: Validate user ID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!userId || !uuidRegex.test(userId)) {
      throw new Error('Invalid user ID');
    }

    // SECURITY: Prevent admin from deleting their own account
    if (userId === adminId) {
      throw new Error('Cannot delete your own account');
    }

    // SECURITY: Check if user exists and get their role
    const { data: user, error: fetchError } = await supabase
      .from('profiles')
      .select('id, role, full_name')
      .eq('id', userId)
      .single();

    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        throw new Error('User not found');
      }
      throw fetchError;
    }

    // SECURITY: Warn if deleting another admin (require confirmation in UI)
    if (user.role === 'admin') {
      console.warn('[USER_SERVICE] Deleting admin user:', user.full_name);
    }

    // SECURITY: Delete profile (CASCADE will handle related records)
    // Note: In production, implement soft delete by adding 'active' column
    const { error: deleteError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (deleteError) {
      throw deleteError;
    }

    // SECURITY: Record rate limit attempt
    userOperationLimiter.recordAttempt(adminId);

    return { success: true, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Delete user error:', error.message);
    return {
      success: false,
      error: new Error(error.message || 'Failed to delete user'),
    };
  }
};

/**
 * Get users by role
 * SECURITY: Admin-only operation, role validation enforced
 * @param {string} role - User role to filter (admin|employee|client)
 * @returns {Promise<{data, error}>}
 */
export const getUsersByRole = async (role) => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Validate role parameter
    if (!role || !VALID_ROLES.includes(role)) {
      throw new Error(`Invalid role. Must be one of: ${VALID_ROLES.join(', ')}`);
    }

    // SECURITY: Parameterized query via Supabase
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, full_name, phone, company_name, created_at, updated_at')
      .eq('role', role)
      .order('full_name', { ascending: true });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Get users by role error:', error.message);
    return {
      data: null,
      error: new Error('Failed to retrieve users'),
    };
  }
};

/**
 * Get user statistics (admin dashboard)
 * SECURITY: Admin-only operation
 * @returns {Promise<{data, error}>}
 */
export const getUserStats = async () => {
  try {
    // SECURITY: Verify admin access
    const { isAdmin, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }

    // SECURITY: Get all profiles (RLS enforces admin access)
    const { data: users, error } = await supabase
      .from('profiles')
      .select('role, created_at');

    if (error) throw error;

    // Calculate statistics
    const stats = {
      total: users.length,
      byRole: {
        admin: users.filter(u => u.role === 'admin').length,
        technician: users.filter(u => u.role === 'technician').length,
        client: users.filter(u => u.role === 'client').length,
      },
      recentUsers: users.filter(u => {
        const createdDate = new Date(u.created_at);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return createdDate >= thirtyDaysAgo;
      }).length,
    };

    return { data: stats, error: null };
  } catch (error) {
    console.error('[USER_SERVICE] Get user stats error:', error.message);
    return {
      data: null,
      error: new Error('Failed to retrieve user statistics'),
    };
  }
};

/**
 * List assignable users by role (for RACI selectors).
 *
 * Unlike getUsersByRole (admin-only), this is callable by any authenticated
 * user. It returns minimal fields needed to populate dropdowns
 * (supervisor / lider pickers on the quotation form). Relies on the
 * `profiles` RLS policy that allows authenticated reads of basic profile
 * fields. Pass `roles` as a string or an array of strings.
 *
 * @param {string | string[]} roles - Role(s) to filter (e.g. 'supervisor', ['supervisor','admin'])
 * @returns {Promise<{data: Array<{id, full_name, role}>, error}>}
 */
export const listAssignableUsers = async (roles) => {
  try {
    const roleList = Array.isArray(roles) ? roles : [roles];
    if (!roleList.length) throw new Error('At least one role is required');

    // Validate every role value
    for (const r of roleList) {
      if (!VALID_ROLES.includes(r)) {
        throw new Error(`Invalid role: ${r}`);
      }
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .in('role', roleList)
      .order('full_name', { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[USER_SERVICE] List assignable users error:', error.message);
    return { data: [], error };
  }
};

/**
 * Restablece la contraseña de OTRO usuario (admin only).
 *
 * Para cuentas sin correo real (código de empleado en el dominio interno) el
 * "¿Olvidaste tu contraseña?" no sirve: el correo no llega. El cambio lo hace
 * la tool MCP `reset_user_password` en el servidor (API admin de Supabase,
 * revoca las sesiones vivas y audita SIN la contraseña). Acá sólo se valida
 * temprano para dar un mensaje claro; el servidor vuelve a validar.
 *
 * @param {string} userId - Usuario destino
 * @param {string} password - Contraseña nueva
 * @returns {Promise<{data, error}>}
 */
export const resetUserPassword = async (userId, password) => {
  try {
    const { isAdmin, userId: adminId, error: accessError } = await verifyAdminAccess();
    if (!isAdmin) {
      throw accessError || new Error('Unauthorized access');
    }
    if (!userId) throw new Error('Falta el usuario');
    if (userId === adminId) {
      throw new Error('Para cambiar tu propia contraseña usá tu perfil');
    }
    const faltan = passwordPolicyErrors(password);
    if (faltan.length) {
      throw new Error(`La contraseña no cumple la política: ${faltan.join(', ').toLowerCase()}`);
    }
    if (userOperationLimiter.isLimited(adminId)) {
      throw new Error('Rate limit exceeded. Please try again later');
    }

    const { data, error } = await callMcpTool('reset_user_password', { user_id: userId, password });
    userOperationLimiter.recordAttempt(adminId);
    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    // Nunca loguear la contraseña: sólo el mensaje.
    console.error('[USER_SERVICE] Reset password error:', error.message);
    return { data: null, error };
  }
};

const userService = {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  getUsersByRole,
  getUserStats,
  listAssignableUsers,
};

export default userService;
